import { useCallback, useEffect, useMemo, useState } from "react";

import api from "../../services/api";
import { useAuth } from "../AuthProvider";
import { useClient } from "../ClientProvider";
import { getPlaces } from "../../utils/getStores";
import { notifyError, notifySucces } from "../../utils/notifications";
import { EligibleProduct, StoreOption } from "./types";
import { isPhysicalIssuerProduct } from "../../utils/isPhysicalIssuerProduct";

type UseChangeClientProductModalParams = {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
};

const useChangeClientProductModal = ({
    open,
    onClose,
    onSuccess,
}: UseChangeClientProductModalParams) => {
    const { client, refreshClientV2 } = useClient();
    const { user } = useAuth();

    const [stores, setStores] = useState<StoreOption[]>([]);
    const [products, setProducts] = useState<EligibleProduct[]>([]);
    const [selectedStoreId, setSelectedStoreId] = useState<number | undefined>();
    const [selectedProductId, setSelectedProductId] = useState<number | undefined>();
    const [loadingStores, setLoadingStores] = useState(false);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

    const currentProductId = client?.product?.codigoProduto;
    const currentProductName = client?.product?.desc_prod || "";
    const isCurrentProductPhysical = isPhysicalIssuerProduct(
        client?.product?.isProdutoDigital
    );
    const isProdutoBandeirado =
        client?.client?.isProdutoBandeirado === true ||
        client?.client?.isProdutoBandeirado === "S";
    const clientId = client?.client?.id;
    const ticketId = client?.client?.ticket?.id;

    const isSingleStore = stores.length === 1;
    const isStoreSelectionDisabled = isSingleStore || loadingStores;

    const selectedStore = useMemo(
        () => stores.find((store) => store.id === selectedStoreId),
        [stores, selectedStoreId]
    );

    const selectedProduct = useMemo(
        () => products.find((product) => product.id === selectedProductId),
        [products, selectedProductId]
    );

    const storeLabel = useMemo(() => {
        if (!selectedStore) return "";
        return selectedStore.nomeFantasia || selectedStore.razaoSocial || String(selectedStore.id);
    }, [selectedStore]);

    const confirmationDescription = useMemo(() => {
        if (!selectedProduct || !storeLabel) return "";

        return `Deseja alterar o produto de "${currentProductName}" para "${selectedProduct.descricao}" na loja ${storeLabel}?`;
    }, [currentProductName, selectedProduct, storeLabel]);

    const resetState = useCallback(() => {
        setSelectedProductId(undefined);
        setProducts([]);
        setStores([]);
        setSelectedStoreId(undefined);
        setIsConfirmationOpen(false);
    }, []);

    const fetchStores = useCallback(async () => {
        setLoadingStores(true);
        try {
            const places = await getPlaces(user);
            const loadedStores = places || [];
            setStores(loadedStores);

            if (loadedStores.length === 1) {
                setSelectedStoreId(loadedStores[0].id);
            }
        } catch (error) {
            console.error("Erro ao buscar lojas:", error);
            notifyError("Falha ao carregar lojas");
            setStores([]);
        } finally {
            setLoadingStores(false);
        }
    }, [user]);

    const fetchEligibleProducts = useCallback(async () => {
        if (!currentProductId) return;

        if (!isCurrentProductPhysical) {
            setProducts([]);
            return;
        }

        setLoadingProducts(true);
        try {
            const response = await api.get("get_products_for_client_change", {
                current_product_id: currentProductId,
                is_produto_bandeirado: isProdutoBandeirado,
            });

            setProducts(response?.data || []);
        } catch (error: any) {
            console.error("Erro ao buscar produtos elegíveis:", error);
            notifyError(error?.message || "Falha ao carregar produtos elegíveis");
            setProducts([]);
        } finally {
            setLoadingProducts(false);
        }
    }, [currentProductId, isCurrentProductPhysical, isProdutoBandeirado]);

    useEffect(() => {
        if (!open) {
            resetState();
            return;
        }

        fetchStores();
        fetchEligibleProducts();
    }, [open, fetchStores, fetchEligibleProducts, resetState]);

    const closeModal = useCallback(() => {
        resetState();
        onClose();
    }, [onClose, resetState]);

    const handleClose = useCallback(() => {
        if (submitting) return;
        closeModal();
    }, [closeModal, submitting]);

    const handleCloseConfirmation = useCallback(() => {
        if (submitting) return;
        setIsConfirmationOpen(false);
    }, [submitting]);

    const validateBeforeConfirm = useCallback(() => {
        if (!isCurrentProductPhysical) {
            notifyError(
                "Alteração permitida apenas de produto físico para produto físico"
            );
            return false;
        }

        if (!clientId || !selectedProductId || !selectedStoreId) {
            notifyError("Selecione a loja e o novo produto para continuar");
            return false;
        }

        if (!selectedProduct) {
            notifyError("Produto selecionado inválido");
            return false;
        }

        return true;
    }, [
        clientId,
        isCurrentProductPhysical,
        selectedProduct,
        selectedProductId,
        selectedStoreId,
    ]);

    const handleRequestConfirm = useCallback(() => {
        if (!validateBeforeConfirm()) return;
        setIsConfirmationOpen(true);
    }, [validateBeforeConfirm]);

    const handleConfirmChange = useCallback(async () => {
        if (!validateBeforeConfirm()) return;

        setSubmitting(true);
        try {
            await api.patch("change_client_product", {
                client_id: clientId,
                ticket_id: ticketId,
                product_id: selectedProductId,
                current_product_id: currentProductId,
                id_loja: selectedStoreId,
                current_product_name: currentProductName,
                new_product_name: selectedProduct!.descricao,
                store_label: storeLabel,
            });

            notifySucces(
                `Produto alterado com sucesso: ${currentProductName} -> ${selectedProduct!.descricao}`
            );

            if (refreshClientV2) {
                await refreshClientV2(true, selectedProductId);
            }
            setIsConfirmationOpen(false);
            onSuccess();
            closeModal();
        } catch (error: any) {
            notifyError(error?.message || "Falha ao alterar produto do client");
        } finally {
            setSubmitting(false);
        }
    }, [
        clientId,
        ticketId,
        closeModal,
        currentProductId,
        currentProductName,
        onSuccess,
        refreshClientV2,
        selectedProduct,
        selectedProductId,
        selectedStoreId,
        storeLabel,
        validateBeforeConfirm,
    ]);

    const canConfirm =
        isCurrentProductPhysical &&
        !!selectedStoreId &&
        !!selectedProductId &&
        !submitting &&
        !loadingProducts;

    return {
        currentProductName,
        isCurrentProductPhysical,
        stores,
        products,
        selectedStoreId,
        setSelectedStoreId,
        selectedProductId,
        setSelectedProductId,
        loadingStores,
        loadingProducts,
        submitting,
        isStoreSelectionDisabled,
        isSingleStore,
        isConfirmationOpen,
        confirmationDescription,
        handleClose,
        handleCloseConfirmation,
        handleRequestConfirm,
        handleConfirmChange,
        canConfirm,
    };
};

export default useChangeClientProductModal;
